import { gsap } from 'gsap/all'
import { DrawSVGPlugin } from '../library/gsap/DrawSVGPlugin'

const GLOBAL_KEY = '__topMovieAreaControllerMounted'
const AREA_SELECTOR = '.js-movie-area'
const READY_ATTRIBUTE = 'data-movie-area-ready'
const ACTIVE_BACKGROUND = 'rgba(0, 0, 0, 0.15)'
const PLAY_LABEL = 'Play'
const PAUSE_LABEL = 'Pause'
const ICON_DRAW_DURATION = 1
const ICON_HOLD_DURATION = 2
const ICON_ERASE_DURATION = 1
const ICON_SHAPES_SELECTOR = 'path, line, polyline, polygon, rect, circle, ellipse'

class TopMovieAreaController {
  constructor(area) {
    this.area = area
    this.icon = area.querySelector('.js-top-movie-icon')
    this.iconShapes = this.icon ? this.icon.querySelectorAll(ICON_SHAPES_SELECTOR) : []
    this.shortVideo = area.querySelector('.js-top-movie-video-short')
    this.mainVideo = area.querySelector('.js-top-movie-video-main')
    this.container = area.querySelector('.js-mouse-stalker-container')
    this.label = area.querySelector('.js-mouse-stalker-label')
    this.indicator = area.querySelector('.js-mouse-stalker-indicator')

    if (!this.shortVideo || !this.mainVideo || !this.container || !this.label || !this.indicator) {
      throw new Error('movie area elements are missing')
    }

    this.defaultLabel = this.label.textContent.trim() || 'Play Movie'
    this.progressRafId = null
    this.iconTimeline = null
    this.isResetting = false

    this.handleActivate = this.handleActivate.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handlePlay = this.handlePlay.bind(this)
    this.handlePause = this.handlePause.bind(this)
    this.handleEnded = this.handleEnded.bind(this)

    this.init()
  }

  init() {
    this.area.setAttribute('role', this.area.getAttribute('role') || 'button')
    this.area.setAttribute('tabindex', this.area.getAttribute('tabindex') || '0')

    this.shortVideo.muted = true
    this.shortVideo.loop = true
    this.shortVideo.playsInline = true
    this.shortVideo.preload = 'auto'

    this.mainVideo.playsInline = true
    this.mainVideo.preload = 'auto'
    this.mainVideo.load()

    this.setupIconAnimation()

    this.area.addEventListener('click', this.handleActivate)
    this.area.addEventListener('keydown', this.handleKeydown)
    this.mainVideo.addEventListener('play', this.handlePlay)
    this.mainVideo.addEventListener('pause', this.handlePause)
    this.mainVideo.addEventListener('ended', this.handleEnded)

    this.resetToLoop()
  }

  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    this.handleActivate()
  }

  handleActivate() {
    if (!this.area.isConnected) {
      return
    }

    if (!this.area.classList.contains('is-main-active')) {
      this.playMain(true)
      return
    }

    if (this.mainVideo.paused || this.mainVideo.ended) {
      this.playMain(false)
      return
    }

    this.pauseMain()
  }

  handlePlay() {
    this.area.classList.add('is-main-active', 'is-main-playing')
    this.area.classList.remove('is-main-paused')
    this.container.style.backgroundColor = ACTIVE_BACKGROUND
    this.setLabel(PAUSE_LABEL)
    this.setAriaLabel('Pause movie')
    this.pauseIconAnimation()
    this.startProgressLoop()
  }

  handlePause() {
    if (this.isResetting || this.mainVideo.ended) {
      return
    }

    this.cancelProgressLoop()
    this.area.classList.add('is-main-active', 'is-main-paused')
    this.area.classList.remove('is-main-playing')
    this.container.style.backgroundColor = ACTIVE_BACKGROUND
    this.setLabel(PLAY_LABEL)
    this.setAriaLabel('Play movie')
    this.restartIconAnimation()
    this.updateProgress()
  }

  handleEnded() {
    this.resetToLoop()
  }

  async playMain(fromStart) {
    this.cancelProgressLoop()
    this.pauseShortLoop()

    if (fromStart) {
      this.setVideoCurrentTime(this.mainVideo, 0)
    }

    this.area.classList.add('is-main-active')
    this.area.classList.remove('is-main-paused')
    this.container.style.backgroundColor = ACTIVE_BACKGROUND
    this.setLabel(PAUSE_LABEL)
    this.setAriaLabel('Pause movie')

    try {
      await this.mainVideo.play()
    } catch (error) {
      this.area.classList.add('is-main-paused')
      this.area.classList.remove('is-main-playing')
      this.setLabel(PLAY_LABEL)
      this.setAriaLabel('Play movie')
      this.restartIconAnimation()
    }
  }

  pauseMain() {
    this.mainVideo.pause()
  }

  resetToLoop() {
    this.isResetting = true
    this.cancelProgressLoop()
    this.mainVideo.pause()
    this.setVideoCurrentTime(this.mainVideo, 0)
    this.setVideoCurrentTime(this.shortVideo, 0)

    this.area.classList.remove('is-main-active', 'is-main-playing', 'is-main-paused')
    this.container.style.removeProperty('background-color')
    this.setLabel(this.defaultLabel)
    this.setAriaLabel('Play movie')
    this.setIndicatorProgress(0)

    const shortPlayPromise = this.shortVideo.play()
    if (shortPlayPromise && typeof shortPlayPromise.catch === 'function') {
      shortPlayPromise.catch(() => {})
    }

    this.restartIconAnimation()
    this.isResetting = false
  }

  pauseShortLoop() {
    this.shortVideo.pause()
  }

  setupIconAnimation() {
    if (!this.iconShapes.length) {
      return
    }

    gsap.registerPlugin(DrawSVGPlugin)
    gsap.set(this.iconShapes, {
      drawSVG: '0% 0%',
    })

    this.iconTimeline = gsap.timeline({
      paused: true,
      repeat: -1,
    })

    this.iconTimeline.to(this.iconShapes, {
      drawSVG: '0% 100%',
      duration: ICON_DRAW_DURATION,
      ease: 'power2.inOut',
      stagger: 0,
    })

    this.iconTimeline.to({}, { duration: ICON_HOLD_DURATION })

    this.iconTimeline.to(this.iconShapes, {
      drawSVG: '100% 100%',
      duration: ICON_ERASE_DURATION,
      ease: 'power2.inOut',
      stagger: 0,
    })
  }

  restartIconAnimation() {
    if (!this.iconTimeline) {
      return
    }

    this.iconTimeline.restart(true)
  }

  pauseIconAnimation() {
    if (!this.iconTimeline) {
      return
    }

    this.iconTimeline.pause()
  }

  setLabel(text) {
    this.label.textContent = text
  }

  setAriaLabel(text) {
    this.area.setAttribute('aria-label', text)
  }

  setVideoCurrentTime(video, time) {
    try {
      video.currentTime = time
    } catch (error) {}
  }

  setIndicatorProgress(progress) {
    const safeProgress = Number.isFinite(progress) ? Math.min(Math.max(progress, 0), 1) : 0
    this.indicator.style.transform = `scaleX(${safeProgress})`
  }

  updateProgress() {
    const duration = Number.isFinite(this.mainVideo.duration) ? this.mainVideo.duration : 0
    const progress = duration > 0 ? this.mainVideo.currentTime / duration : 0
    this.setIndicatorProgress(progress)
  }

  startProgressLoop() {
    this.cancelProgressLoop()

    const tick = () => {
      this.updateProgress()

      if (!this.mainVideo.paused && !this.mainVideo.ended) {
        this.progressRafId = window.requestAnimationFrame(tick)
      } else {
        this.progressRafId = null
      }
    }

    tick()
  }

  cancelProgressLoop() {
    if (this.progressRafId == null) {
      return
    }

    window.cancelAnimationFrame(this.progressRafId)
    this.progressRafId = null
  }
}

const initArea = (area) => {
  if (!(area instanceof HTMLElement) || area.getAttribute(READY_ATTRIBUTE) === 'true') {
    return
  }

  const hasRequiredElements = area.querySelector('.js-top-movie-video-short') && area.querySelector('.js-top-movie-video-main') && area.querySelector('.js-mouse-stalker-container') && area.querySelector('.js-mouse-stalker-label') && area.querySelector('.js-mouse-stalker-indicator')

  if (!hasRequiredElements) {
    return
  }

  area.setAttribute(READY_ATTRIBUTE, 'true')

  try {
    new TopMovieAreaController(area)
  } catch (error) {
    area.removeAttribute(READY_ATTRIBUTE)
  }
}

const initMovieAreas = (root = document) => {
  if (root instanceof HTMLElement && root.matches(AREA_SELECTOR)) {
    initArea(root)
  }

  if (!(root instanceof Element) && root !== document) {
    return
  }

  root.querySelectorAll(AREA_SELECTOR).forEach(initArea)
}

const observeMovieAreas = () => {
  if (!document.body) {
    return
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return
        }

        initMovieAreas(node)
      })
    })
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })
}

const boot = () => {
  initMovieAreas()
  observeMovieAreas()
}

export default function initTopMovieArea() {
  if (typeof window === 'undefined' || window[GLOBAL_KEY]) {
    return
  }

  window[GLOBAL_KEY] = true

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true })
  } else {
    boot()
  }
}
